import { useEffect } from "react";

/** Decorative follower only: the browser pointer is never hidden. */
export default function ElasticCursor() {
  useEffect(() => {
    const fine = matchMedia("(hover:hover) and (pointer:fine) and (min-width:768px)"), reduced = matchMedia("(prefers-reduced-motion:reduce)");
    const cursor = document.createElement("div"), shape = document.createElement("span"), icon = document.createElement("span");
    cursor.className = "ibt-cursor"; cursor.setAttribute("aria-hidden", "true"); shape.className = "ibt-cursor-shape"; icon.className = "ibt-cursor-icon";
    cursor.append(shape, icon); document.body.append(cursor);
    let x = 0, y = 0, tx = 0, ty = 0, seen = false, raf = 0, last = 0, target: Element | null = null;
    const enabled = () => fine.matches && !reduced.matches;
    const hide = () => { seen = false; cursor.classList.remove("is-visible"); cancelAnimationFrame(raf); raf = 0; last = 0; };
    const update = () => {
      if (!target) return;
      const native = target.closest("input:not([type=range]),textarea,select,[contenteditable=true]");
      const player = target.closest(".ibt-video-player"), bounds = player?.getBoundingClientRect();
      const isVideo = !!player && !!target.closest(".ibt-video-hit") && ty < bounds!.bottom - 108 && !target.closest("[data-revealing]");
      const layer = document.fullscreenElement || target.closest("dialog[open]") || document.body;
      if (cursor.parentElement !== layer) layer.append(cursor);
      cursor.classList.toggle("is-visible", !native && seen);
      cursor.classList.toggle("is-link", !!target.closest("a[href],button,input[type=range],[role=button]"));
      cursor.classList.toggle("is-video", isVideo);
      if (isVideo) icon.textContent = player!.querySelector("video")?.paused ? "▶" : "Ⅱ";
    };
    const tick = (now: number) => {
      const dt = last ? Math.min(48, now - last) : 16.67; last = now;
      const dx = tx - x, dy = ty - y, distance = Math.hypot(dx, dy), follow = 1 - Math.exp(-dt / 65);
      x += dx * follow; y += dy * follow;
      const stretch = Math.min(distance / 250, .32), angle = Math.atan2(dy, dx) * 180 / Math.PI;
      cursor.style.transform = `translate3d(${x}px,${y}px,0)`;
      shape.style.transform = `rotate(${angle}deg) scale(${1 + stretch},${1 - stretch})`;
      if (distance > .08) raf = requestAnimationFrame(tick); else { shape.style.transform = "none"; raf = 0; last = 0; }
    };
    const move = (event: PointerEvent) => {
      if (event.pointerType !== "mouse" || !enabled()) { hide(); return; }
      target = event.target instanceof Element ? event.target : null; tx = event.clientX; ty = event.clientY;
      if (!seen) { x = tx; y = ty; seen = true; } update(); if (!raf) raf = requestAnimationFrame(tick);
    };
    const leave = (event: PointerEvent) => { if (!event.relatedTarget) hide(); };
    const visibility = () => { if (document.hidden) hide(); };
    const scroll = () => { if (seen) { target = document.elementFromPoint(tx, ty); update(); } };
    document.addEventListener("pointermove", move, { passive: true }); document.addEventListener("pointerout", leave);
    document.addEventListener("keydown", hide); document.addEventListener("visibilitychange", visibility);
    document.addEventListener("play", update, true); document.addEventListener("pause", update, true);
    window.addEventListener("blur", hide); window.addEventListener("scroll", scroll, { passive: true });
    fine.addEventListener("change", hide); reduced.addEventListener("change", hide);
    return () => {
      hide(); cursor.remove(); document.removeEventListener("pointermove", move); document.removeEventListener("pointerout", leave);
      document.removeEventListener("keydown", hide); document.removeEventListener("visibilitychange", visibility);
      document.removeEventListener("play", update, true); document.removeEventListener("pause", update, true);
      window.removeEventListener("blur", hide); window.removeEventListener("scroll", scroll);
      fine.removeEventListener("change", hide); reduced.removeEventListener("change", hide);
    };
  }, []);
  return null;
}
